/** The parts of a click target this predicate needs. */
export interface CheckboxClickTarget {
  tagName?: string;
  type?: string;
}

/**
 * True when a click came from Radix's hidden checkbox input rather than a person.
 *
 * Radix `Checkbox.Root` renders a hidden `<input type="checkbox">` alongside the
 * visible control, but ONLY when the checkbox sits inside a `<form>`
 * (`isFormControl = !!form || !!control.closest("form")`). Whenever `checked`
 * changes, that input re-dispatches a **bubbling** click so uncontrolled forms
 * behave natively.
 *
 * Any row that wraps a checkbox in its own click handler therefore catches that
 * synthetic click as though the person had clicked again: the handler toggles,
 * `checked` flips, the input re-dispatches, and it loops until React gives up
 * with "Maximum update depth exceeded". The same row works perfectly outside a
 * form, because then no hidden input exists — which is why this only bites on
 * some screens.
 *
 * A real click never targets the hidden input: it is `aria-hidden` and sits
 * behind the visible control, so a person's click lands on the button or its
 * contents. Ignoring input-targeted clicks is therefore precise — it drops the
 * synthetic re-dispatch and nothing else, leaving a click on the checkbox itself
 * working normally.
 */
export function isCheckboxBubbleClick(
  target: CheckboxClickTarget | null | undefined,
): boolean {
  if (!target) return false;
  return (
    target.tagName?.toUpperCase() === "INPUT" && target.type === "checkbox"
  );
}
